/**
 * Por qué la vista no puede mostrar el reporte. Los tres primeros llegan como
 * `data.code` del backend y se arreglan en pantallas distintas; el cuarto no es
 * un error sino un `200` con alcance vacío.
 */
export type RouteReportBlockedReason =
  | 'integration_disabled'
  | 'company_required'
  | 'scope_not_configured'
  | 'no_distributors'
  | 'generic';

/** Códigos del contrato que mapean a una pantalla de bloqueo propia. */
export const ROUTE_REPORT_BLOCKED_REASON_BY_CODE: Record<
  string,
  RouteReportBlockedReason
> = {
  'company.tiendaPerfectaIntegrationDisabled': 'integration_disabled',
  'company.tiendaPerfectaCompanyRequiredWhenIntegrationEnabled':
    'company_required',
  'role.tiendaPerfectaScopeNotConfigured': 'scope_not_configured',
};

/* ─────────────────────────── Estados ───────────────────────────
   Todo el mapeo de estados del backend vive acá y en ningún otro lado: son los
   valores que hay que tocar si upstream renombra alguno.                      */

/** `status` de una ruta. `closed` y `return_declared` se tratan igual. */
export type RouteStatus =
  | 'not_started'
  | 'in_progress'
  | 'closed'
  | 'return_declared';

/**
 * `status` de una parada. El contrato solo enumera `finished` y `pending`;
 * `in_progress` está asumido por coherencia con `RouteStatus` y falta
 * confirmarlo con el backend.
 */
export type StopStatus = 'pending' | 'in_progress' | 'finished';

/** Clave de UI de una ruta. Un valor desconocido cae en `not_started`. */
export type RouteUiState = 'not_started' | 'on_route' | 'finished';

/**
 * Clave de UI de una parada. `finished` se abre en dos según el bypass: una
 * parada cerrada por excepción se muestra "No ejecutada" con su motivo, aunque
 * para el backend esté igual de terminada y ya cuente en `completedStops`.
 */
export type StopUiState = 'pending' | 'on_going' | 'executed' | 'not_executed';

export const ROUTE_UI_STATE_BY_STATUS: Record<string, RouteUiState> = {
  not_started: 'not_started',
  in_progress: 'on_route',
  closed: 'finished',
  return_declared: 'finished',
};

export const getRouteUiState = (status: string): RouteUiState =>
  ROUTE_UI_STATE_BY_STATUS[status] ?? 'not_started';

export const getStopUiState = (
  status: string,
  hasBypass: boolean
): StopUiState => {
  if (status === 'finished') return hasBypass ? 'not_executed' : 'executed';
  if (status === 'in_progress') return 'on_going';
  return 'pending';
};

/** Colores de tag de antd por estado de ruta. */
export const ROUTE_STATE_TAG_COLOR: Record<RouteUiState, string> = {
  not_started: 'gold',
  on_route: 'blue',
  finished: 'green',
};

/** Colores de tag de antd por estado de parada. */
export const STOP_STATE_TAG_COLOR: Record<StopUiState, string> = {
  executed: 'cyan',
  not_executed: 'gold',
  on_going: 'blue',
  pending: 'default',
};

/**
 * Colores del timeline. Son SEMÁNTICOS y por eso fijos: no pueden salir de
 * `colorPrimary`, que cambia por compañía. Un primario rojo pintaría "ejecutada"
 * con el color de un error.
 */
export const TIMELINE_COLORS = {
  executed: '#00BFB3',
  notExecuted: '#FAAD14',
  onGoing: '#91CAFF',
  pending: '#EDEDED',
} as const;

/**
 * Debajo de este ancho el eje horario deja de ser usable: a 320px da 22px por
 * hora y las marcas quedan a 12px entre sí, la mitad del objetivo táctil mínimo
 * de WCAG 2.2. Ahí el timeline pasa a barra segmentada.
 */
export const TIMELINE_COMPACT_MAX_WIDTH = 760;
